import { useCallback, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { getApiBaseUrl, getSocketIoPath } from '../../../../core/config/api';
import { Intervention } from '../../../../shared/types/game';
import type { InstanceRow } from '../interventions/instanceModel';
import { mergeInterventionRow } from '../interventions/instanceModel';
import { interventionSocketOwnerId } from './buildingDetailsTabHelpers';

type TranslateFn = (key: string) => string;

interface UseBuildingDetailsDataOpts {
  userId: number | undefined;
  t: TranslateFn;
}

/**
 * Hook extrait de BuildingDetailsTab.tsx :
 * - charge interventions JSON + instances CTA
 * - toggle prise-appels
 * - socket.io live updates
 */
export function useBuildingDetailsData({ userId, t }: UseBuildingDetailsDataOpts) {
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [ctaInstances, setCtaInstances] = useState<InstanceRow[]>([]);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [socketLive, setSocketLive] = useState(false);
  const [priseAppels, setPriseAppels] = useState(false);
  const [priseLoadError, setPriseLoadError] = useState<string | null>(null);
  const [priseToggling, setPriseToggling] = useState(false);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const loadCtaInstances = useCallback(async () => {
    if (!userId) return;
    setCtaLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/cta/interventions`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.interventions)) {
        setCtaInstances(data.interventions as InstanceRow[]);
      }
    } catch {
      /* ignore */
    } finally {
      setCtaLoading(false);
    }
  }, [userId, authHeaders]);

  const refreshCtaInstances = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/cta/interventions`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.interventions)) {
        setCtaInstances(data.interventions as InstanceRow[]);
      }
    } catch {
      /* ignore */
    }
  }, [userId, authHeaders]);

  const reloadBuildingInterventions = useCallback(async () => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    if (!token) return;
    try {
      const iRes = await fetch(`${getApiBaseUrl(true)}/buildings/interventions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (iRes.ok) {
        const idata = await iRes.json();
        if (idata.success && Array.isArray(idata.data)) {
          setInterventions(idata.data);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const loadPriseAppels = useCallback(async () => {
    if (!userId) return;
    setPriseLoadError(null);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/cta/prise-appels`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success && typeof data.prise_appels === 'boolean') {
        setPriseAppels(data.prise_appels);
      } else {
        setPriseLoadError(data.error || t('interventionSidebar.priseLoadError'));
      }
    } catch {
      setPriseLoadError(t('interventionSidebar.priseLoadError'));
    }
  }, [userId, authHeaders, t]);

  const togglePriseAppels = useCallback(async () => {
    if (!userId || priseToggling) return;
    setPriseToggling(true);
    setPriseLoadError(null);
    const next = !priseAppels;
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/cta/prise-appels`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ prise_appels: next }),
      });
      const data = await res.json();
      if (data.success && typeof data.prise_appels === 'boolean') {
        setPriseAppels(data.prise_appels);
      } else {
        setPriseLoadError(data.error || t('interventionSidebar.priseToggleError'));
      }
    } catch {
      setPriseLoadError(t('interventionSidebar.priseToggleError'));
    } finally {
      setPriseToggling(false);
    }
  }, [userId, priseToggling, priseAppels, authHeaders, t]);

  useEffect(() => {
    void reloadBuildingInterventions();
  }, [reloadBuildingInterventions]);

  useEffect(() => {
    void loadCtaInstances();
  }, [loadCtaInstances]);

  useEffect(() => {
    void loadPriseAppels();
  }, [loadPriseAppels]);

  useEffect(() => {
    if (!userId) {
      setSocketLive(false);
      return;
    }

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    const socket = io({
      path: getSocketIoPath(),
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });

    const upsertCta = (payload: InstanceRow) => {
      if (!payload?.id) return;
      setCtaInstances((prev) => {
        const i = prev.findIndex((x) => x.id === payload.id);
        if (i === -1) return [payload, ...prev];
        const next = [...prev];
        next[i] = mergeInterventionRow(next[i], payload);
        return next;
      });
      void reloadBuildingInterventions();
    };

    const onConnect = () => {
      setSocketLive(true);
      socket.emit('join:user', userId);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', () => setSocketLive(false));

    socket.on('intervention:new', (payload: InstanceRow) => {
      if (interventionSocketOwnerId(payload) !== userId) return;
      upsertCta(payload);
    });

    socket.on('intervention:update', (payload: InstanceRow) => {
      if (interventionSocketOwnerId(payload) !== userId) return;
      upsertCta(payload);
    });

    socket.on('cta:prise-appels', (data: { prise_appels?: boolean }) => {
      if (typeof data?.prise_appels === 'boolean') {
        setPriseAppels(data.prise_appels);
        setPriseLoadError(null);
      }
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [userId, reloadBuildingInterventions]);

  return {
    interventions,
    setInterventions,
    ctaInstances,
    setCtaInstances,
    ctaLoading,
    socketLive,
    priseAppels,
    priseLoadError,
    priseToggling,
    togglePriseAppels,
    authHeaders,
    loadCtaInstances,
    refreshCtaInstances,
    reloadBuildingInterventions,
  };
}
